import type { ReactElement } from "react";
import { useMemo, useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { Button, Combobox, DropdownButton, Modal, Textfield } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { TopicOptionLabel } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/prompts-table/PromptsTable.styled";
import { allTrackers } from "app/services/track/track";

interface IAddPromptModalHook {
    openModal: (topic: string) => void;
    modal: ReactElement;
}

export const useAddPromptModal = (
    topics: string[],
    allPrompts: string[],
    onCreatePrompt: (text: string, topic: string) => void,
): IAddPromptModalHook => {
    const [isOpen, setOpen] = useState<boolean>(false);
    const [isTopicsDropdownOpen, setTopicsDropdownOpen] = useState<boolean>(false);
    const [selectedTopic, setSelectedTopic] = useState<string>("");
    const [prompt, setPrompt] = useState<string>("");
    const isSaveAvailable = useMemo<boolean>(() => {
        if (!isOpen) {
            return false;
        }

        const trimmedPrompt = prompt?.trim()?.toLowerCase() ?? "";
        const alreadyExists = allPrompts?.some((query) => query.toLowerCase() === trimmedPrompt);
        const isEmpty = !selectedTopic || !trimmedPrompt;
        return !alreadyExists && !isEmpty;
    }, [prompt, selectedTopic, allPrompts, isOpen]);

    const openModal = (topic: string): void => {
        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "add prompt/open");
        setSelectedTopic(topic);
        setOpen(true);
    };

    const onTopicClick = (topic: string): void => {
        setTopicsDropdownOpen(false);
        setSelectedTopic(topic);
    };

    const closeModal = (): void => {
        setOpen(false);
        setTopicsDropdownOpen(false);
        setSelectedTopic("");
        setPrompt("");
    };

    const submit = (): void => {
        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "add prompt/submit");
        onCreatePrompt(prompt, selectedTopic);
        closeModal();
    };

    return {
        openModal,
        modal: (
            <Modal
                isOpen={isOpen}
                onRequestClose={closeModal}
                shouldCloseOnEsc
                shouldCloseOnOverlayClick
                customStyles={{
                    content: {
                        width: "400px",
                    },
                }}
            >
                <Modal.Header>
                    <Modal.Title tooltip={translate("llm.prompts.edit.prompts-table.create.tooltip")}>
                        {translate("llm.prompts.edit.prompts-table.create.title")}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Content>
                    <Flex
                        flexDirection="column"
                        padding="16px 0 24px 0"
                        gap="24px"
                    >
                        <Flex
                            flexDirection="column"
                            gap="16px"
                        >
                            <Flex
                                flexDirection="column"
                                gap="4px"
                            >
                                <Text
                                    fontVariant="caption"
                                    color={colorsPalettes.carbon[300]}
                                >
                                    {translate("llm.prompts.edit.prompts-table.modal.field.prompt.title")}
                                </Text>
                                <Textfield
                                    placeholder={translate(
                                        "llm.prompts.edit.prompts-table.modal.field.prompt.placeholder",
                                    )}
                                    onChange={(value) => setPrompt(value)}
                                    autoFocus
                                />
                            </Flex>
                            <Flex
                                flexDirection="column"
                                gap="4px"
                            >
                                <Text
                                    fontVariant="caption"
                                    color={colorsPalettes.carbon[300]}
                                >
                                    {translate("llm.prompts.edit.prompts-table.modal.field.topic.title")}
                                </Text>
                                <Combobox
                                    isOpen={isTopicsDropdownOpen}
                                    onOpenChange={setTopicsDropdownOpen}
                                    placement="bottom"
                                    matchTriggerWidth
                                    data-automation="prompts-add-topic-dropdown"
                                >
                                    <Combobox.Trigger>
                                        <Flex width="100%">
                                            <DropdownButton isOpen={isTopicsDropdownOpen}>
                                                <Text
                                                    fontVariant="input2"
                                                    color={colorsPalettes.carbon[500]}
                                                >
                                                    {selectedTopic}
                                                </Text>
                                            </DropdownButton>
                                        </Flex>
                                    </Combobox.Trigger>
                                    <Combobox.Content>
                                        {topics.map((topic) => (
                                            <Combobox.Option
                                                key={topic}
                                                id={topic}
                                                textValue={topic}
                                                onClick={() => onTopicClick(topic)}
                                            >
                                                <TopicOptionLabel>{topic}</TopicOptionLabel>
                                            </Combobox.Option>
                                        ))}
                                    </Combobox.Content>
                                </Combobox>
                            </Flex>
                        </Flex>
                    </Flex>
                </Modal.Content>
                <Modal.Footer>
                    <Button
                        type="flat"
                        onClick={closeModal}
                    >
                        {translate("global.cancel")}
                    </Button>
                    <Button
                        type="primary"
                        onClick={submit}
                        isDisabled={!isSaveAvailable}
                    >
                        {translate("common.submit")}
                    </Button>
                </Modal.Footer>
            </Modal>
        ),
    };
};
